/** @jsxImportSource @emotion/react */
import { Modal } from "../../../../components/Modal";
import { PrimaryButton } from "../../../../components/Buttons";
import { colors } from "../../../../components/colors";
import { useParams } from "react-router-dom";
import { Order } from "../../model/OrderSuccessResponse";
import { toastr } from "react-redux-toastr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../auth/authSelectors";
import { yupResolver } from "@hookform/resolvers/yup";
import { transformPriceFromAPI } from "../../../../util";
import {
  PriceInput,
  transformPriceInput,
} from "../../../../components/forms/inputs/PriceInput";
import { css } from "@emotion/react";
import { getErrorMessage } from "../../../../error-handler";
import { useRefundOrder } from "../service/order-api";

const refundSchema = yup.object().shape({
  amountToRefund: yup
    .string()
    .required("Please enter amount to refund to continue"),
});

export type RefundOrderForm = {
  amountToRefund: number;
};

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  order: Order;
};

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const RefundOrderConfirmModal = ({
  isOpen,
  closeModal,
  order,
}: Props) => {
  const { id } = useParams<{
    id: string;
  }>();
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);
  const methods = useForm<RefundOrderForm>({
    resolver: yupResolver(refundSchema),
    defaultValues: {
      amountToRefund: transformPriceFromAPI(
        order.total.amount,
        order.total.currency
      ),
    },
  });
  const { mutateAsync: refundOrder, isLoading } = useRefundOrder(id);

  const { handleSubmit } = methods;

  const onSubmit = async (data: RefundOrderForm) => {
    try {
      await refundOrder({
        organizationKeyName: organization,
        data: {
          orderId: id!,
          amount: transformPriceInput(
            data.amountToRefund,
            order.total.currency
          ),
        },
      });
      toastr.success("Success", "Order refunded.");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    } finally {
      closeModal();
    }
  };

  return (
    <FormProvider {...methods}>
      <Modal
        header="Refund order"
        subtitle="How much do you want to refund?"
        isOpen={isOpen}
        onClose={() => {
          methods.reset();
          closeModal();
        }}
      >
        <form css={formStyle}>
          <PriceInput
            formMethods={methods}
            label="Amount to refund"
            name="amountToRefund"
            currencyString={order.total.currency}
            helpertext="Enter the amount you want to refund"
          />
          <PrimaryButton
            color={colors.white}
            backgroundColor={colors.ctaMain}
            hoverColor={colors.ctaSecondaryOne}
            disabled={isLoading}
            onClick={handleSubmit(onSubmit)}
            type="button"
            width="100%"
          >
            {isLoading ? (
              <FontAwesomeIcon icon={solid("spinner")} spin pulse />
            ) : (
              "Refund order"
            )}
          </PrimaryButton>
        </form>
      </Modal>
    </FormProvider>
  );
};
